/**
 * Transport layer for the Media Matchers endpoints.
 *
 * Two hard constraints shape this file:
 *  - CORS blocks the JSON response in some browsers, so every Search call needs
 *    a JSONP fallback.
 *  - The visitor navigates away immediately after submitting, so every Post has
 *    to go out three ways at once and report whichever lands first.
 */

const SEARCH_TIMEOUT_MS = 8000

/** Results arrive under any of five shapes depending on the endpoint. */
export function parseResults(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[]
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>
    for (const key of ['results', 'schools', 'data', 'result']) {
      const val = obj[key]
      if (Array.isArray(val)) return val as Record<string, unknown>[]
      // `data` and `result` are sometimes a single object rather than a list.
      if (val && typeof val === 'object' && (key === 'data' || key === 'result'))
        return [val as Record<string, unknown>]
    }
  }
  return []
}

/** Brand name lives under a few different keys; entries missing it render as empty cards. */
export function getBrandName(school: Record<string, unknown>): string {
  for (const key of [
    'mBrandName',
    'brandName',
    'schoolName',
    'name',
    'brand',
    'mSchoolName',
  ]) {
    const val = school[key]
    if (typeof val === 'string' && val.trim()) return val.trim()
  }
  return ''
}

export function withBrandName(
  schools: Record<string, unknown>[],
): Record<string, unknown>[] {
  return schools.filter((s) => getBrandName(s) !== '')
}

let jsonpCounter = 0

/** JSONP fallback — the endpoints accept `?callback=`. */
function jsonp(url: string): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const name = `__mmJsonp${Date.now()}_${jsonpCounter++}`
    const script = document.createElement('script')
    const cleanup = () => {
      delete (window as unknown as Record<string, unknown>)[name]
      script.remove()
      clearTimeout(timer)
    }
    const timer = setTimeout(() => {
      cleanup()
      reject(new Error('JSONP timeout'))
    }, SEARCH_TIMEOUT_MS)
    ;(window as unknown as Record<string, unknown>)[name] = (data: unknown) => {
      cleanup()
      resolve(data)
    }
    script.onerror = () => {
      cleanup()
      reject(new Error('JSONP failed'))
    }
    script.src = `${url}${url.includes('?') ? '&' : '?'}callback=${name}`
    document.body.appendChild(script)
  })
}

/**
 * GET a Search endpoint: fetch with an 8s abort, then fall back to JSONP.
 * Returns null when both transports fail so callers can show a retry state.
 */
export async function searchRequest(
  label: string,
  baseUrl: string,
  params: URLSearchParams,
): Promise<unknown | null> {
  const url = `${baseUrl}?${params.toString()}`
  console.log(`[MM:${label}] GET`, url)

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), SEARCH_TIMEOUT_MS)
  try {
    const res = await fetch(url, { method: 'GET', signal: controller.signal })
    if (res.ok) {
      const text = await res.text()
      try {
        return JSON.parse(text)
      } catch {
        console.warn(`[MM:${label}] Unparseable body, trying JSONP`)
      }
    } else {
      console.warn(`[MM:${label}] HTTP ${res.status}, trying JSONP`)
    }
  } catch (err) {
    console.warn(`[MM:${label}] fetch failed, trying JSONP`, err)
  } finally {
    clearTimeout(timer)
  }

  try {
    const data = await jsonp(url)
    console.log(`[MM:${label}] JSONP ok`)
    return data
  } catch (err) {
    console.error(`[MM:${label}] JSONP failed`, err)
    return null
  }
}

/**
 * Sends a Post endpoint. Every Media Matchers endpoint — including the "post"
 * ones — is called with GET and query-string params; nothing uses an HTTP POST
 * or a request body.
 *
 * It goes out three ways in parallel (keepalive fetch, image pixel, JSONP) and
 * resolves on the first confirmation, because the visitor navigates away
 * immediately. The guard keeps it to a single report even when all three land.
 */
export function postRequest(
  label: string,
  baseUrl: string,
  params: URLSearchParams,
): Promise<boolean> {
  const url = `${baseUrl}?${params.toString()}`
  console.log(`[MM:${label}] GET`, url)

  return new Promise((resolve) => {
    let settled = false
    const done = (via: string, ok: boolean) => {
      if (settled) return
      settled = true
      console.log(`[MM:${label}] confirmed via ${via}: ${ok}`)
      resolve(ok)
    }

    // The three transports run SEQUENTIALLY, not in parallel: firing them all
    // at once delivered the same lead three times, and the partner would have
    // to dedupe on mRecordID to avoid triple-counting it.
    //
    // A keepalive fetch already survives the page unloading, so it is the only
    // request in the normal case. The pixel and JSONP exist purely as fallbacks
    // for the browsers where fetch is blocked outright.
    fetch(url, { method: 'GET', mode: 'no-cors', keepalive: true })
      .then(() => done('fetch', true))
      .catch(() => {
        console.warn(`[MM:${label}] fetch blocked, retrying via pixel`)
        sendPixel(url)
          .then(() => done('pixel', true))
          .catch(() => {
            console.warn(`[MM:${label}] pixel failed, retrying via JSONP`)
            jsonp(url)
              .then(() => done('jsonp', true))
              .catch(() => done('none', false))
          })
      })

    // Nothing reported back in time. The opaque no-cors response cannot be
    // inspected, so a silent send is treated as delivered rather than retried —
    // retrying here is what would duplicate the lead.
    setTimeout(() => done('timeout', true), SEARCH_TIMEOUT_MS)
  })
}

/**
 * A Post whose RESPONSE BODY we need to read (e.g. TCPA Post's `leadValid`).
 *
 * Exactly ONE request goes out. The endpoint rejects a repeat of the same
 * mRecordID with a 400, so any "retry" here would either double-deliver the
 * lead or log a spurious failure — a request that was sent but unreadable has
 * still been delivered.
 */
export async function postRequestReadable(
  label: string,
  baseUrl: string,
  params: URLSearchParams,
): Promise<{ delivered: boolean; data: unknown | null }> {
  const url = `${baseUrl}?${params.toString()}`
  console.log(`[MM:${label}] GET (readable)`, url)

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), SEARCH_TIMEOUT_MS)
  try {
    const res = await fetch(url, { method: 'GET', signal: controller.signal })
    const text = await res.text()
    if (!res.ok) {
      console.warn(`[MM:${label}] HTTP ${res.status}`, text)
      return { delivered: false, data: null }
    }
    try {
      const data = JSON.parse(text)
      console.log(`[MM:${label}] response:`, data)
      return { delivered: true, data }
    } catch {
      console.warn(`[MM:${label}] delivered, body not JSON:`, text)
      return { delivered: true, data: null }
    }
  } catch (err) {
    // A CORS-blocked or aborted request has still reached the server, so it is
    // treated as delivered and NOT resent. The caller sees data: null and
    // continues down the normal path.
    console.warn(`[MM:${label}] response unreadable (request was sent)`, err)
    return { delivered: true, data: null }
  } finally {
    clearTimeout(timer)
  }
}

/** Fires a 1x1 image request and resolves when the browser loads it. */
function sendPixel(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img')
    img.width = 1
    img.height = 1
    img.style.display = 'none'
    img.alt = ''
    img.onload = () => {
      img.remove()
      resolve()
    }
    img.onerror = () => {
      img.remove()
      reject(new Error('pixel failed'))
    }
    img.src = url
    document.body.appendChild(img)
  })
}
